import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Copy, LayoutDashboard, Loader2, Pencil, Plus, RefreshCw, Send, Trash2, Users } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import { DocumentDialog, GuardedModal, useDialogs, useDirtySnapshot, usePageLabel } from '@onvice/portal-ui';

/** Admin-UI: Seiten-Vorlagen verwalten (Joerg-Wunsch 2026-06-08).
 *  Mandant-Vorlagen voll editierbar; Onvice-System-Vorlagen (tenant_id IS NULL)
 *  read-only, koennen aber als Ausgangspunkt geklont werden.
 *
 *  Seit 2026-09-30 (Regel „Dokument-Navigation", Abhakliste 1.47): Der Vorlagen-Dialog
 *  ist ein Dialog für ein ganzes Dokument (E5) — öffnet in der Anzeige, „Bearbeiten"
 *  schaltet um, „Speichern" zurück in die Anzeige. Neu/Klonen/Pushen sind Dialoge für
 *  kurze Aktionen mit Schutz (E6): Klick daneben, X und Escape fragen bei Eingaben
 *  nach. Löschen fragt über das Portal-Dialogfenster nach. */

interface PageTemplate {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  suggested_for_groups: string[];
  sort_order: number;
  updated_at: string;
  is_system: boolean;
}

interface Group {
  id: string;
  name: string;
}

/** Arbeitskopie der Vorlagen-Metadaten im Dokument-Dialog. */
interface TemplateMeta {
  name: string;
  description: string;
  icon: string;
  groups: string[];
}

const INPUT = 'w-full px-3 py-2 rounded-lg border border-default bg-card text-foreground';
const BTN_PRIMARY = 'px-4 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm disabled:opacity-50 inline-flex items-center gap-1 min-h-[44px]';
const BTN_GHOST = 'px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground min-h-[44px]';

export default function AdminPageTemplatesPage() {
  usePageLabel('Seiten-Vorlagen', 'list');
  const dlg = useDialogs();
  const [templates, setTemplates] = useState<PageTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [editTarget, setEditTarget] = useState<PageTemplate | null>(null);
  const [cloneTarget, setCloneTarget] = useState<PageTemplate | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [pushTarget, setPushTarget] = useState<PageTemplate | null>(null);

  const load = useCallback(() => {
    api.get<{ success: boolean; data: PageTemplate[] }>('/portal/admin/page-templates')
      .then((r) => { if (r.success) { setTemplates(r.data); setError(null); } else setError('Vorlagen konnten nicht geladen werden'); })
      .catch((e: Error) => setError(e.message || 'Vorlagen konnten nicht geladen werden'));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get<{ success: boolean; data: Group[] }>('/portal/admin/adressbuch/groups')
      .then((r) => { if (r.success) setGroups(r.data); })
      .catch(() => { /* Gruppen sind optional (Empfehlung) — Liste bleibt leer */ });
  }, []);

  const own = useMemo(() => templates?.filter((t) => !t.is_system) ?? [], [templates]);
  const system = useMemo(() => templates?.filter((t) => t.is_system) ?? [], [templates]);

  async function requestDelete(t: PageTemplate) {
    const ok = await dlg.confirm({
      title: `Vorlage „${t.name}“ löschen?`,
      message: 'Die Vorlage wird endgültig gelöscht. Persönliche Seiten, die daraus entstanden sind, bleiben bestehen — „Auf Vorlage zurücksetzen“ funktioniert für sie danach nicht mehr.',
      confirmLabel: 'Endgültig löschen',
      danger: true,
    });
    if (!ok) return;
    try {
      const r = await api.delete<{ success: boolean; error?: string }>(`/portal/admin/page-templates/${t.id}`);
      if (!r.success) throw new Error(r.error ?? 'Löschen fehlgeschlagen');
      load();
    } catch (e) {
      await dlg.error('Vorlage wurde nicht gelöscht', e);
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-test-anchor="seiten-vorlagen">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <LayoutDashboard size={22} /> Seiten-Vorlagen
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vorlagen erscheinen im „Neue Seite"-Dialog beim User. Mandant-Vorlagen sind editierbar, onvice-System-Vorlagen nur lesend (per „Klonen" kopierst du sie in den Mandanten).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setNewOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm min-h-[44px]"
          data-test-anchor="vorlage-neu"
        >
          <Plus size={14} /> Neue Vorlage
        </button>
      </header>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex flex-wrap items-center gap-2" data-test-anchor="vorlagen-ladefehler">
          <AlertCircle size={16} /> Nicht geladen: {error}
          <button type="button" onClick={load} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-500/40 text-xs hover:bg-red-500/10">
            <RefreshCw size={12} /> Erneut laden
          </button>
        </div>
      )}

      <section>
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground mb-2">Mandant-Vorlagen</h2>
        {!templates ? (
          error ? null : <div className="flex items-center justify-center py-6"><Loader2 size={20} className="animate-spin text-muted-foreground" /></div>
        ) : own.length === 0 ? (
          <div className="text-sm text-muted-foreground border border-dashed border-default rounded-lg py-6 text-center">
            Noch keine eigenen Vorlagen. Lege oben rechts eine neue an oder klone eine onvice-Vorlage.
          </div>
        ) : (
          <TemplateList items={own} groupsById={groups} onOpen={setEditTarget} onDelete={(t) => void requestDelete(t)} onPush={setPushTarget} />
        )}
      </section>

      <section>
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground mb-2">onvice-Vorlagen (nur lesend)</h2>
        {!templates ? null : system.length === 0 ? (
          <div className="text-sm text-muted-foreground">Keine onvice-Vorlagen vorhanden.</div>
        ) : (
          <TemplateList items={system} groupsById={groups} readOnly onClone={setCloneTarget} onPush={setPushTarget} />
        )}
      </section>

      <TemplateDialog
        template={editTarget}
        groups={groups}
        onClose={() => setEditTarget(null)}
        onChanged={load}
      />

      <NewTemplateDialog
        open={newOpen}
        groups={groups}
        onClose={() => setNewOpen(false)}
        onCreated={() => { setNewOpen(false); load(); }}
      />

      <CloneDialog
        source={cloneTarget}
        onClose={() => setCloneTarget(null)}
        onCloned={() => { setCloneTarget(null); load(); }}
      />

      <PushDialog
        template={pushTarget}
        groups={groups}
        onClose={() => setPushTarget(null)}
      />
    </div>
  );
}

function TemplateList({ items, groupsById, readOnly, onOpen, onDelete, onClone, onPush }: {
  items: PageTemplate[];
  groupsById: Group[];
  readOnly?: boolean;
  onOpen?: (t: PageTemplate) => void;
  onDelete?: (t: PageTemplate) => void;
  onClone?: (t: PageTemplate) => void;
  onPush?: (t: PageTemplate) => void;
}) {
  const nameOf = (id: string) => groupsById.find((g) => g.id === id)?.name ?? id.slice(0, 6);
  return (
    <ul className="divide-y divide-default rounded-lg border border-default overflow-hidden bg-card">
      {items.map((t) => (
        <li key={t.id} className="flex items-center gap-2 p-3 hover:bg-muted transition-colors" data-test-anchor={`vorlage-${t.id}`}>
          <LayoutDashboard size={16} className="text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate flex items-center gap-2">
              {t.name}
              {readOnly && <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">System</span>}
            </div>
            {t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <Users size={11} className="text-muted-foreground shrink-0" />
              {t.suggested_for_groups.length === 0 ? (
                <span className="text-[11px] text-muted-foreground italic">Keine Gruppen-Empfehlung — nur über Picker oder Push</span>
              ) : (
                t.suggested_for_groups.map((gid) => (
                  <span
                    key={gid}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-brand-500/10 text-brand-300 border border-brand-500/30"
                    title="Wird beim Erst-Login dieser Gruppe automatisch angelegt"
                  >
                    {nameOf(gid)}
                  </span>
                ))
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onPush?.(t)}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-brand-300 hover:bg-background min-h-[40px]"
            title="Diese Vorlage an alle Mitglieder einer Gruppe als persönliche Seite anhängen"
          >
            <Send size={14} /> Pushen
          </button>
          {!readOnly ? (
            <>
              <Link
                to={`/admin/page-templates/${t.id}`}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-background min-h-[40px]"
                title="Layout der Vorlage bearbeiten"
              >
                <LayoutDashboard size={14} /> Layout
              </Link>
              <button
                type="button"
                onClick={() => onOpen?.(t)}
                className="p-2 text-muted-foreground hover:text-foreground rounded hover:bg-background min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                title="Name / Beschreibung / Empfehlung anzeigen"
                aria-label="Vorlage öffnen"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => onDelete?.(t)}
                className="p-2 text-muted-foreground hover:text-rose-400 rounded hover:bg-background min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                title="Vorlage löschen"
                aria-label="Vorlage löschen"
              >
                <Trash2 size={14} />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onClone?.(t)}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-background min-h-[40px]"
              title="Vorlage in den Mandanten kopieren (dann editierbar)"
            >
              <Copy size={14} /> Klonen
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Dialog für ein ganzes Dokument (E5): Anzeige zuerst, „Bearbeiten" schaltet um.
// ---------------------------------------------------------------------------
function TemplateDialog({ template, groups, onClose, onChanged }: {
  template: PageTemplate | null;
  groups: Group[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const baseline = useMemo<TemplateMeta | null>(() => template ? {
    name: template.name,
    description: template.description ?? '',
    icon: template.icon ?? 'LayoutDashboard',
    groups: [...template.suggested_for_groups].sort(),
  } : null, [template]);
  const nameOf = (id: string) => groups.find((g) => g.id === id)?.name ?? id.slice(0, 6);

  return (
    <DocumentDialog<TemplateMeta>
      open={template != null}
      title={template ? `Vorlage „${template.name}“` : 'Vorlage'}
      label="Vorlage"
      id={template?.id ?? 'none'}
      baseline={baseline}
      size="md"
      testAnchor="vorlage-dialog"
      validate={(v) => (v.name.trim() ? [] : ['Name fehlt.'])}
      onSave={async (v) => {
        if (!template) return;
        const r = await api.patch<{ success: boolean; error?: string }>(`/portal/admin/page-templates/${template.id}`, {
          name: v.name.trim(),
          description: v.description.trim() || null,
          icon: v.icon.trim() || null,
          suggestedForGroups: v.groups,
        });
        if (!r.success) throw new Error(r.error ?? 'Speichern fehlgeschlagen');
        return { ...v, name: v.name.trim(), description: v.description.trim(), icon: v.icon.trim() };
      }}
      onChanged={onChanged}
      onClose={onClose}
      renderRead={(v) => (
        <dl className="space-y-3 text-sm">
          <div><dt className="text-xs text-muted-foreground">Name</dt><dd>{v.name}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Beschreibung</dt><dd>{v.description || <span className="text-muted-foreground">—</span>}</dd></div>
          <div><dt className="text-xs text-muted-foreground">Icon (lucide-Name)</dt><dd className="font-mono text-xs">{v.icon || '—'}</dd></div>
          <div>
            <dt className="text-xs text-muted-foreground">Empfohlen für Gruppen</dt>
            <dd>{v.groups.length === 0 ? <span className="text-muted-foreground">keine</span> : v.groups.map(nameOf).join(', ')}</dd>
          </div>
        </dl>
      )}
      renderEdit={(v, setField) => (
        <div className="space-y-4">
          <Field label="Name">
            <input type="text" value={v.name} onChange={(e) => setField('name', e.target.value)} className={INPUT} />
          </Field>
          <Field label="Beschreibung (optional)">
            <textarea value={v.description} onChange={(e) => setField('description', e.target.value)} rows={2} className={INPUT} />
          </Field>
          <Field label="Icon (lucide-Name, z.B. LayoutDashboard)">
            <input type="text" value={v.icon} onChange={(e) => setField('icon', e.target.value)} className={INPUT} />
          </Field>
          <Field label="Empfohlen für Gruppen">
            <GroupPicker groups={groups} picked={v.groups} onChange={(next) => setField('groups', next)} />
            <div className="text-[11px] text-muted-foreground mt-1">Beim Erst-Login werden Vorlagen, die mit mind. einer Gruppe des Users übereinstimmen, automatisch in seine Seiten übernommen.</div>
          </Field>
        </div>
      )}
    />
  );
}

function GroupPicker({ groups, picked, onChange }: { groups: Group[]; picked: string[]; onChange: (next: string[]) => void }) {
  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next].sort());
  };
  return (
    <div className="max-h-48 overflow-y-auto border border-default rounded-lg divide-y divide-default">
      {groups.length === 0 ? (
        <div className="text-sm text-muted-foreground p-3">Keine Gruppen vorhanden.</div>
      ) : groups.map((g) => (
        <label key={g.id} className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer">
          <input type="checkbox" checked={picked.includes(g.id)} onChange={() => toggle(g.id)} />
          <span className="text-sm">{g.name}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Neuanlage (Annahme 1: schließt nach „Anlegen"), mit Dialog-Schutz (E6).
// ---------------------------------------------------------------------------
function NewTemplateDialog({ open, groups, onClose, onCreated }: {
  open: boolean;
  groups: Group[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = useDirtySnapshot(open, { name, description, picked });

  useEffect(() => {
    if (open) { setName(''); setDescription(''); setPicked([]); setError(null); }
  }, [open]);

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ success: boolean; data: { id: string }; error?: string }>('/portal/admin/page-templates', {
        name: name.trim(),
        description: description.trim() || null,
        icon: 'LayoutDashboard',
        suggestedForGroups: picked,
      });
      if (r.success) onCreated(); else setError(r.error ?? 'Anlegen fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GuardedModal
      open={open}
      title="Neue Vorlage"
      label="Neue Vorlage"
      dirty={dirty}
      busy={busy}
      onClose={onClose}
      testAnchor="vorlage-neu-dialog"
      footer={(requestClose) => (
        <>
          <button type="button" onClick={requestClose} disabled={busy} className={BTN_GHOST}>Abbrechen</button>
          <button type="button" onClick={() => void create()} disabled={busy || !name.trim()} className={BTN_PRIMARY}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Anlegen
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <Field label="Name">
          <input type="text" value={name} autoFocus onChange={(e) => setName(e.target.value)} className={INPUT} placeholder="z.B. Meister-Tagesüberblick" />
        </Field>
        <Field label="Beschreibung (optional)">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={INPUT} />
        </Field>
        <Field label="Empfohlen für Gruppen">
          <GroupPicker groups={groups} picked={picked} onChange={setPicked} />
        </Field>
        <div className="text-xs text-muted-foreground">
          Die Vorlage wird leer angelegt. Layout fügst du nach dem Anlegen über „Layout" hinzu.
        </div>
        {error && <div className="text-sm text-red-500" data-test-anchor="vorlage-neu-fehler">{error}</div>}
      </div>
    </GuardedModal>
  );
}

function CloneDialog({ source, onClose, onCloned }: {
  source: PageTemplate | null;
  onClose: () => void;
  onCloned: () => void;
}) {
  const open = source != null;
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = useDirtySnapshot(open, name, source?.id);

  useEffect(() => {
    if (source) { setName(`${source.name} (Kopie)`); setError(null); }
  }, [source]);

  const clone = async () => {
    if (!source) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ success: boolean; data: { id: string }; error?: string }>('/portal/admin/page-templates', {
        name: name.trim(),
        cloneFromId: source.id,
        suggestedForGroups: source.suggested_for_groups,
      });
      if (r.success) onCloned(); else setError(r.error ?? 'Klonen fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GuardedModal
      open={open}
      title={source ? `„${source.name}“ klonen` : 'Klonen'}
      label="Kopie der Vorlage"
      dirty={dirty}
      busy={busy}
      onClose={onClose}
      size="sm"
      testAnchor="vorlage-klonen-dialog"
      footer={(requestClose) => (
        <>
          <button type="button" onClick={requestClose} disabled={busy} className={BTN_GHOST}>Abbrechen</button>
          <button type="button" onClick={() => void clone()} disabled={busy || !name.trim()} className={BTN_PRIMARY}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
            Klonen
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Layout, Icon, Beschreibung und Gruppen-Empfehlung werden übernommen. Die Kopie gehört dem Mandanten und ist anschließend voll editierbar.
        </p>
        <Field label="Name der Kopie">
          <input type="text" value={name} autoFocus onChange={(e) => setName(e.target.value)} className={INPUT} />
        </Field>
        {error && <div className="text-sm text-red-500">{error}</div>}
      </div>
    </GuardedModal>
  );
}

// ---------------------------------------------------------------------------
// Kurze Aktion (E6): Pushen an eine Gruppe.
// ---------------------------------------------------------------------------
function PushDialog({ template, groups, onClose }: {
  template: PageTemplate | null;
  groups: Group[];
  onClose: () => void;
}) {
  const open = template != null;
  const [groupId, setGroupId] = useState<string>('');
  const [includeExisting, setIncludeExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ pushed: number; skipped: number; totalUsers: number } | null>(null);
  // Vor dem Push fragt der Dialog bei geänderter Auswahl nach; nach dem Ergebnis nicht mehr.
  const dirty = useDirtySnapshot(open, { groupId, includeExisting }, template?.id) && result == null;

  useEffect(() => {
    if (template) {
      setGroupId(template.suggested_for_groups[0] ?? groups[0]?.id ?? '');
      setIncludeExisting(false); setError(null); setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const groupName = groups.find((g) => g.id === groupId)?.name ?? '';

  const push = async () => {
    if (!template) return;
    if (!groupId) { setError('Bitte eine Gruppe auswählen.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ success: boolean; data?: { pushed: number; skipped: number; totalUsers: number }; error?: string }>(
        `/portal/admin/page-templates/${template.id}/push`,
        { groupId, includeExisting },
      );
      if (r.success && r.data) setResult(r.data);
      else setError(r.error ?? 'Push fehlgeschlagen');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GuardedModal
      open={open}
      title={template ? `„${template.name}“ an Gruppe pushen` : 'Pushen'}
      label="Push-Auswahl"
      dirty={dirty}
      busy={busy}
      onClose={onClose}
      testAnchor="vorlage-push-dialog"
      footer={(requestClose) => result ? (
        <button type="button" onClick={onClose} className={BTN_PRIMARY}>Schließen</button>
      ) : (
        <>
          <button type="button" onClick={requestClose} disabled={busy} className={BTN_GHOST}>Abbrechen</button>
          <button type="button" onClick={() => void push()} disabled={busy || !groupId} className={BTN_PRIMARY}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Pushen
          </button>
        </>
      )}
    >
      {result ? (
        <div className="px-4 py-3 rounded-lg bg-brand-500/10 border border-brand-500/40 text-brand-200 text-sm space-y-1" data-test-anchor="vorlage-push-ergebnis">
          <div><strong>{result.pushed}</strong> Seite{result.pushed === 1 ? '' : 'n'} angelegt</div>
          {result.skipped > 0 && <div className="text-brand-300/80">{result.skipped} User übersprungen (Seite war bereits vorhanden)</div>}
          <div className="text-brand-300/80">Gruppe „{groupName}“ hat {result.totalUsers} Mitglied{result.totalUsers === 1 ? '' : 'er'} mit User-Account</div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Die Vorlage wird als persönliche Seite ans Ende der Seiten-Liste jedes Gruppen-Mitglieds angehängt. User können sie danach umbenennen, anpassen oder löschen — die Vorlage selbst bleibt unverändert.
          </p>
          <Field label="Ziel-Gruppe">
            {groups.length === 0 ? (
              <div className="text-sm text-muted-foreground">Keine Gruppen vorhanden.</div>
            ) : (
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={INPUT}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}
          </Field>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeExisting}
              onChange={(e) => setIncludeExisting(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">Auch bei Usern, die die Seite schon haben, erneut anhängen</span>
              <span className="block text-[11px] text-muted-foreground">Standard: bestehende Empfänger werden übersprungen. Aktivieren, wenn du den aktuellen Stand der Vorlage als zusätzliche neue Seite ausrollen willst.</span>
            </span>
          </label>
          {error && <div className="text-sm text-red-500">{error}</div>}
        </div>
      )}
    </GuardedModal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}
